import React, { useCallback, useContext } from 'react';
import shortid from 'shortid';
import EDDatePicker from '../../../../../../BaseComponents/DatePicker/EDDatePicker';
import EDDialog from '../../../../../../BaseComponents/Dialog/EDDialog';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import { EDTextEdit } from '../../../../../../BaseComponents/TextEdit/EDTextEdit';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import { dateTimeStringIsValidDate } from '../../../../../../Utils/dateUtils';
import { LoginStateContext } from '../../../../../Login/Contexts/LoginStateContext/LoginStateContext';
import {
  IBusinessDataFormContext,
  useBusinessDataFormContext,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { MeQuery_me } from '../../../Rutiner/Kalender/Queries/MeQuery/_QueryTypes/MeQuery';
import { AktivitetQuery_node_TEDBOAktivitet__Associations_Bevakning_All } from '../Queries/_QueryTypes/AktivitetQuery';

interface IProps {
  open: boolean;
  setOpenBevakning: React.Dispatch<React.SetStateAction<boolean>>;
  formContext: IBusinessDataFormContext<any>;
  meData: MeQuery_me[] | null | undefined;
}

export const BevakningDialog: React.FC<React.PropsWithChildren<IProps>> = (
  props: IProps
) => {
  const { setOpenBevakning } = props;
  const localContext = useBusinessDataFormContext();
  const formStateDispatch = localContext?.formStateDispatch;
  const parentFormStateDispatch =
    localContext?.parentFormContext?.formStateDispatch;
  const currentDataValues = localContext?.formState.currentValues;
  const loginStateContext = useContext(LoginStateContext);

  const today = new Date();
  const dateString = today.toISOString().split('T')[0];

  const closeDialog = useCallback(() => {
    formStateDispatch?.({ type: 'CANCEL' });

    setOpenBevakning(false);
  }, [formStateDispatch, setOpenBevakning]);

  const removeBevakning = () => {
    localContext?.parentFormContext?.formState?.currentValues?._Associations?.Bevakning_All?.forEach(
      (
        bevakning: AktivitetQuery_node_TEDBOAktivitet__Associations_Bevakning_All
      ) => {
        localContext?.parentFormContext?.update([
          {
            operation: 'OwnFields',
            values: {
              BEVAKNING: false,
            },
          },
          {
            operation: 'OwnFields',
            values: {
              BEVAKNING_DATUM: null,
            },
          },
          {
            operation: 'OwnFields',
            values: {
              BEVAKNING_TEXT: null,
            },
          },
          {
            operation: 'Delete_Values',
            associationName: 'Bevakning',
            id: bevakning?.id,
          },
        ]);
      }
    );
    localContext?.formStateDispatch({
      type: 'CANCEL',
    });
    props.setOpenBevakning(false);
  };

  const setBevakning = useCallback(() => {
    localContext?.parentFormContext?.update([
      {
        operation: 'OwnFields',
        values: {
          BEVAKNING: true,
        },
      },
    ]);
    if (localContext?.formState.values) {
      parentFormStateDispatch?.({
        type: 'UPDATE',
        value: localContext.formState.values,
      });
    }
    formStateDispatch?.({
      type: 'CANCEL',
    });

    setOpenBevakning(false);
  }, [
    localContext?.formState.values,
    localContext?.parentFormContext,
    formStateDispatch,
    parentFormStateDispatch,
    setOpenBevakning,
  ]);

  let removeHyphens =
    currentDataValues?.BEVAKNING_DATUM &&
    currentDataValues?.BEVAKNING_DATUM.replaceAll('-', '');

  let isValidDate = dateTimeStringIsValidDate(removeHyphens);

  const getBevakningsDatumError = (
    bevakningsDatum: string | undefined,
    dateString: string,
    isValidDate: boolean
  ): string | undefined => {
    if (!bevakningsDatum) {
      return undefined;
    }

    if (!isValidDate) {
      return `Ogiltigt värde "${bevakningsDatum}" i Bevakningsdatum`;
    }

    if (bevakningsDatum < dateString) {
      return `Bevakningsdatum måste vara "${dateString}" eller senare`;
    }

    return undefined;
  };

  return (
    <EDDialog
      open={props.open}
      title="Bevakning"
      fullWidth={true}
      onClose={() => {
        props.setOpenBevakning(false);
      }}
      footerActions={[
        {
          label: 'avbryt',
          onClick: closeDialog,
        },
        {
          label: 'ta bort',
          onClick: removeBevakning,
          buttonProps: {
            disabled: !currentDataValues?.BEVAKNING,
          },
        },
        {
          label: 'använd',
          onClick: setBevakning,
          buttonProps: {
            disabled:
              currentDataValues?.BEVAKNING_DATUM &&
              (currentDataValues?.BEVAKNING_DATUM < dateString || !isValidDate),
            disabledReason:
              currentDataValues?.BEVAKNING_DATUM &&
              (currentDataValues?.BEVAKNING_DATUM < dateString ||
                !isValidDate) &&
              'Ogiltigt värde i bevakningsdatum',
          },
        },
      ]}
      onEntered={() => {
        if (
          !localContext?.formState?.currentValues?._Associations?.Bevakning_All?.find(
            (
              x: AktivitetQuery_node_TEDBOAktivitet__Associations_Bevakning_All
            ) =>
              x?._Associations?.Anvandare?.SIGNATURE === loginStateContext.sign
          )
        ) {
          localContext?.update([
            {
              operation: 'Add_Values',
              associationName: 'Bevakning',
              id: shortid(),
              values: { _typ: 'ANVANDARE' },
            },
            {
              operation: 'Ref',
              associationName: 'Anvandare',
              id: shortid(),
              values: {
                id: props.meData?.[0].userId,
                SIGNATURE: loginStateContext.sign,
              },
            },
          ]);
        }
      }}
      testId="bevakningDialog"
    >
      <EDBox sx={{ display: 'flex', flexDirection: 'column' }}>
        <EDBox sx={{ width: '50%' }}>
          <EDDatePicker
            label="Bevakningsdatum "
            tooltip="Startdatum för bevakning"
            allowOnlyNumbers
            fullWidth
            disablePastDates
            value={
              currentDataValues?.BEVAKNING_DATUM &&
              currentDataValues?.BEVAKNING_DATUM
            }
            onChange={newBevakningDatum => {
              localContext?.update([
                {
                  operation: 'OwnFields',
                  values: {
                    BEVAKNING_DATUM: newBevakningDatum,
                  },
                },
              ]);
            }}
            helperText={getBevakningsDatumError(
              currentDataValues?.BEVAKNING_DATUM,
              dateString,
              isValidDate
            )}
            testId="bevakasAvDatePicker"
            error={
              currentDataValues?.BEVAKNING_DATUM &&
              (currentDataValues?.BEVAKNING_DATUM < dateString || !isValidDate)
            }
          />
        </EDBox>
        <EDTextEdit
          label={'Text'}
          value={
            currentDataValues?.BEVAKNING_TEXT &&
            currentDataValues?.BEVAKNING_TEXT
          }
          fullWidth
          onChange={newBevakningText => {
            localContext?.update([
              {
                operation: 'OwnFields',
                values: {
                  BEVAKNING_TEXT: newBevakningText,
                },
              },
            ]);
          }}
          testId="bevakasAvText"
        />
        {currentDataValues?.BEVAKNING && currentDataValues?.BevakasAv && (
          <EDTypography testId="bevakasAvSign" variant="subtitle2">
            Bevakas av: {currentDataValues?.BevakasAv}
          </EDTypography>
        )}
      </EDBox>
    </EDDialog>
  );
};
